/**
 * Slot materials (contract §2.7): MAT_hull, MAT_emissive, MAT_glass and MAT_decal for one preset tier, shared by every
 * look: the livery reaches the shader as per-object uniforms (context.ts `createPalette`, M6 P23).
 * `configure*` (re)writes a material's node graph in place, so a preset change recompiles the same material objects
 * (meshes keep their references; one pipeline per slot × geometry variant, plus the first_rush trim set for trimmed
 * hulls; never per look or per ship).
 */
import { MeshPhysicalNodeMaterial, MeshStandardNodeMaterial, type Node } from 'three/webgpu'
import {
  Fn,
  If,
  abs,
  attribute,
  color,
  dot,
  float,
  floor,
  fwidth,
  length,
  max,
  mix,
  mx_cell_noise_float,
  mx_noise_float,
  positionLocal,
  positionView,
  positionViewDirection,
  normalView,
  saturate,
  select,
  smoothstep,
  texture,
  uv,
  vec3,
} from 'three/tsl'
import type { PresetTier } from '../data'
import type { HullContext, PaletteUniforms, TrimFamily, Variant } from './context'
import { ENGINE_KIND, MAX_ENGINES, MAX_LIGHTS } from './objectData'
import { litOnly } from './litOnly'
import { decalInk, fbm01, hullSurface, rimTerm, unitColorY, weathering } from './surface'

type F = Node<'float'>
type V3 = Node<'vec3'>

export function createHullMaterial(family: TrimFamily, variantName: string): MeshPhysicalNodeMaterial {
  const m = new MeshPhysicalNodeMaterial()
  m.name = `hull:${family}:${variantName}`
  return m
}

export function configureHull(m: MeshPhysicalNodeMaterial, ctx: HullContext, pal: PaletteUniforms, family: TrimFamily, tier: PresetTier, variant: Variant): void {
  const s = hullSurface(ctx, pal, family, tier, variant)
  // L-M4 C1 (shadows.litOnlyColor): the albedo graph stays out of the shadow passes; its `.a` is 1 either way.
  m.colorNode = ctx.data.shadows.litOnlyColor ? litOnly(s.color) : s.color
  m.roughnessNode = s.roughness
  m.metalnessNode = s.metalness
  m.aoNode = s.ao
  m.normalNode = s.normal
  m.emissiveNode = s.emissive
  m.clearcoatNode = s.clearcoat
  m.clearcoatRoughnessNode = s.clearcoatRoughness
  m.envMapIntensity = ctx.data.globals.hullEnvIntensity
  m.needsUpdate = true
}

/** Unpacks a light slot's linear colour (objectData.packLinearColor) in the shader. */
function unpackColor(w: F): V3 {
  const v = abs(w)
  return vec3(floor(v.div(65536)), floor(v.div(256)).mod(256), v.mod(256)).div(255)
}

/**
 * MAT_emissive: each texel belongs to the nearest socket within reach. Main nozzle throats follow the E2 drive band
 * (`Y = 3 + 12 × thrust`, × 1.4 on boost; auxiliaries × auxMult), retro rings light only while braking, RCS lenses stay
 * dark (their puffs are A11's), LIGHT_ lenses glow E1 in the socket's colour (blinking ones stay dim between the
 * sprite's flashes), anything else is the livery's idle emitter (lit windows, status lamps).
 */
export function emissiveGlow(ctx: HullContext, pal: PaletteUniforms): V3 {
  const data = ctx.data
  const e = data.emissive
  const o = ctx.obj
  const p = positionLocal
  const idle = unitColorY(pal.uEmissiveIdle, pal.uEmissiveIdleY)
  const engineSlot = (i: number, best: F, glow: V3): [F, V3] => {
    const pos = o.engPos[i]
    const dir = o.engDir[i]
    const kind = dir.w
    const r = pos.w.max(0.05)
    const d = length(p.sub(pos.xyz)).div(r.mul(e.engineLensReach))
    const aux = select(r.lessThan(e.auxRadius), float(e.auxMult), float(1))
    const y = select(kind.lessThan(ENGINE_KIND.main + 0.5), o.ship.x.mul(aux), select(kind.lessThan(ENGINE_KIND.retro + 0.5), o.ship.y, float(0)))
    const take = d.lessThan(best).and(kind.greaterThan(ENGINE_KIND.none + 0.5))
    return [select(take, d, best), select(take, unitColorY(pal.uEngineCore, y), glow)]
  }
  const lightSlot = (i: number, best: F, glow: V3): [F, V3] => {
    const l = o.lights[i]
    const d = length(p.sub(l.xyz)).div(e.lightLensReach)
    const take = d.lessThan(best).and(abs(l.w).greaterThan(0.5))
    const y = select(l.w.lessThan(0), float(e.blinkLensY), float(e.signalY))
    return [select(take, d, best), select(take, unitColorY(unpackColor(l.w), y), glow)]
  }
  if (data.shaderPerf.boundSlotLoops) {
    // G5 (PERF): slots past the object's engine / light counts are parked (kind none, colour 0) and never take, so
    // a uniform branch per slot skips them with an identical result.
    return Fn(() => {
      const best = float(1).toVar()
      const glow = vec3(idle).toVar()
      for (let i = 0; i < MAX_ENGINES; i++) {
        If(o.counts.x.greaterThan(i), () => {
          const [b, g] = engineSlot(i, best, glow)
          glow.assign(g)
          best.assign(b)
        })
      }
      for (let i = 0; i < MAX_LIGHTS; i++) {
        If(o.counts.z.greaterThan(i), () => {
          const [b, g] = lightSlot(i, best, glow)
          glow.assign(g)
          best.assign(b)
        })
      }
      return glow
    })() as unknown as V3
  }
  let best: F = float(1)
  let glow: V3 = idle
  for (let i = 0; i < MAX_ENGINES; i++) [best, glow] = engineSlot(i, best, glow)
  for (let i = 0; i < MAX_LIGHTS; i++) [best, glow] = lightSlot(i, best, glow)
  return glow
}

export function createEmissiveMaterial(): MeshStandardNodeMaterial {
  const m = new MeshStandardNodeMaterial()
  m.name = 'emissive'
  return m
}

export function configureEmissive(m: MeshStandardNodeMaterial, ctx: HullContext, pal: PaletteUniforms): void {
  // The dark lens (style guide §3.6: an emitter's off state is a dark lens, never painted-on light).
  m.colorNode = color(ctx.data.globals.lensOff)
  m.roughnessNode = float(0.25)
  m.metalnessNode = float(0)
  m.emissiveNode = emissiveGlow(ctx, pal)
  m.envMapIntensity = ctx.data.globals.hullEnvIntensity
  m.needsUpdate = true
}

export function createGlassMaterial(): MeshPhysicalNodeMaterial {
  const m = new MeshPhysicalNodeMaterial()
  m.name = 'glass'
  return m
}

/**
 * Canopy glass (style guide §4): dark tint, F0 ≈ 0.04, env 1.0, plus the livery's E0 instrument glow behind it (livery
 * `glassGlowY` 0 = dark glass: the glow terms multiply to zero, the pipeline stays shared).
 */
export function configureGlass(m: MeshPhysicalNodeMaterial, ctx: HullContext, pal: PaletteUniforms): void {
  const g = ctx.data.globals
  m.colorNode = ctx.data.shadows.litOnlyColor ? litOnly(pal.uGlassTint) : pal.uGlassTint
  m.metalnessNode = float(0)
  m.roughnessNode = float(g.glassRoughness)
  m.ior = g.glassIor
  m.envMapIntensity = g.glassEnvIntensity
  let emissive: V3 = rimTerm(ctx, pal, float(1)).mul(0.5)
  const glow: V3 = unitColorY(pal.uGlassGlow, pal.uGlassGlowY)
  {
    const eye = ctx.obj.eye
    // Station windows (no pilot eye socket): object-space window cells, a share of them lit warm (E0), each at its
    // own level, so a hab ring reads as inhabited or recently abandoned at range.
    const cellP = floor(positionLocal.div(g.windowCell))
    const pick = mx_cell_noise_float(cellP.add(vec3(ctx.obj.params.x.mul(13), 0, 0)))
    const level = mx_cell_noise_float(cellP.add(vec3(7.1, 3.3, 1.9))).mul(0.8).add(0.4)
    const windows = select(pick.lessThan(g.windowLitShare), level, float(0)).mul(float(1).sub(eye.w))
    emissive = emissive.add(glow.mul(windows))
    // Instrument displays sit below the pilot's eye line: blocky lit patches in the lower pane, brightest near the
    // panel, visible from outside only within about 20 m (Kestrel sheet: faint E0, Y 0.3 at the brightest).
    const p = positionLocal
    const panel = eye.xyz.add(vec3(0, -0.35, -0.6))
    const near = float(1).sub(smoothstep(0.2, 1.1, length(p.sub(panel))))
    const below = smoothstep(eye.y.add(0.1), eye.y.sub(0.45), p.y)
    const screens = smoothstep(0.45, 0.6, mx_cell_noise_float(floor(vec3(p.x.mul(7), p.y.mul(9), p.z.mul(5)))))
    const facing = saturate(dot(normalView, positionViewDirection)).sqrt()
    const range = float(1).sub(smoothstep(8, 20, positionView.length()))
    const amount = near.mul(below).mul(screens.mul(0.8).add(0.2)).mul(facing).mul(range).mul(eye.w)
    emissive = emissive.add(glow.mul(amount))
  }
  m.emissiveNode = emissive
  m.needsUpdate = true
}

/** Decal depth offset magnitudes: one depth-slope step plus four depth units toward the camera. */
const DECAL_OFFSET_FACTOR = 1
const DECAL_OFFSET_UNITS = 4

/**
 * The polygon offset that pulls decal quads toward the camera. three's WebGPU and WebGL2 backends hand
 * `polygonOffsetFactor`/`Units` to the GPU unchanged (WebGPU `depthBiasSlopeScale`/`depthBias`, `gl.polygonOffset`),
 * where the bias is added to the fragment depth. With the game's reversed depth buffer (renderer.ts: `greater` is
 * nearer) the offset must be positive. The old fixed −1/−4 pushed decals one depth-slope step BEHIND the hull, which
 * beats their 1–2 mm float on every oblique face: the hero (`q_front`) lost every decal on High and Low while the
 * face-on orthos kept them (K10).
 */
export function decalDepthOffset(reversedDepth: boolean): { factor: number; units: number } {
  const sign = reversedDepth ? 1 : -1
  return { factor: sign * DECAL_OFFSET_FACTOR, units: sign * DECAL_OFFSET_UNITS }
}

/** `reversedDepth`: the renderer's `reversedDepthBuffer` after init (the WebGL2 fallback clears it without clip control). */
export function createDecalMaterial(variantName: string, reversedDepth: boolean): MeshPhysicalNodeMaterial {
  const m = new MeshPhysicalNodeMaterial({ transparent: true, depthWrite: false })
  m.name = `decal:${variantName}`
  // Decal quads float 1–2 mm off the hull (contract §4.5); the offset keeps them on top at range and grazing angles.
  const offset = decalDepthOffset(reversedDepth)
  m.polygonOffset = true
  m.polygonOffsetFactor = offset.factor
  m.polygonOffsetUnits = offset.units
  return m
}

/**
 * MAT_decal: the SDF atlas (A = distance, 0.5 = edge) inked in the faction colours: the quad's zone picks the ink pair
 * and the atlas R picks within it; hand-painted operator art (atlas G = 0) gets a noise-distorted threshold (style
 * guide §7.7) and soft cells (atlas B = 0, the scorch fan) use A as opacity.
 * Decals chip, fade and soot together with the paint under them (style guide §7.1). A carrier atlas hides the slot.
 */
export function configureDecal(m: MeshPhysicalNodeMaterial, ctx: HullContext, pal: PaletteUniforms, tier: PresetTier, variant: Variant): void {
  const data = ctx.data
  const atlasMap = ctx.sets.decals?.maps.baseColor
  m.visible = atlasMap !== undefined
  if (!atlasMap) {
    m.needsUpdate = true
    return
  }
  const uv0 = uv()
  const atlas = texture(atlasMap, uv0)
  const zone = attribute('color', 'vec4').a.mul(3).round()
  const p = positionLocal
  const oct = tier.noiseOctaves
  // Per-texel atlas flags (tex.decals, A6): G = 1 crisp stencil edge / 0 hand-painted; B = 1 SDF / 0 soft opacity.
  const hand = float(1).sub(saturate(atlas.g))
  const sdfKind = saturate(atlas.b)
  const jitter = oct > 0 ? mx_noise_float(p.mul(9)).mul(data.decals.distortion).mul(hand) : float(0)
  const threshold = float(0.5).add(jitter)
  const aw = fwidth(atlas.a).mul(0.75).max(1e-4)
  const sdfAlpha = smoothstep(threshold.sub(aw), threshold.add(aw), atlas.a)
  let alpha: F = mix(atlas.a, sdfAlpha, sdfKind)
  const w = weathering(ctx, pal, tier, { baked: variant.baked, trimmed: variant.trimmed }, true)
  // Paint wear eats the decal with the paint (chips to primer read as gaps in the ink).
  const wearAmt = saturate(pal.uWear.mul(ctx.tune.wear))
  const chip = fbm01(p.mul(w.scale.mul(data.wear.chipFrequency * 1.6)).add(w.seed.mul(71)), oct)
  const eaten = smoothstep(float(1.02).sub(wearAmt.mul(0.45)), float(1.1).sub(wearAmt.mul(0.45)), chip).mul(w.near)
  alpha = alpha.mul(float(1).sub(eaten)).mul(float(1).sub(w.scorch.mul(0.8)))
  let ink: V3 = decalInk(pal, zone, atlas.r)
  const grime = saturate(w.cavity.mul(pal.uGrimeAmount).mul(ctx.tune.grime))
  ink = mix(ink, pal.uGrime, grime.mul(0.6))
  ink = mix(ink, pal.uSoot, w.soot.mul(0.88))
  ink = mix(ink, color(data.globals.scorch), w.scorch.mul(0.9))
  m.colorNode = ink
  m.opacityNode = alpha
  m.metalnessNode = float(0)
  m.roughnessNode = pal.uPaintRoughness.x.add(data.decals.roughnessOffset).add(w.soot.mul(0.3))
  m.emissiveNode = rimTerm(ctx, pal, float(1))
  m.envMapIntensity = data.globals.hullEnvIntensity
  if (tier.clearcoat) {
    m.clearcoatNode = ctx.tune.clearcoat.mul(pal.uGloss).mul(float(1).sub(w.soot)).mul(max(float(1).sub(eaten), 0))
    m.clearcoatRoughnessNode = ctx.tune.clearcoatRoughness
  } else {
    m.clearcoatNode = null
    m.clearcoatRoughnessNode = null
  }
  m.needsUpdate = true
}

